/**
 * Lightweight FPS / frame-time counter for the render loop.
 *
 * Call `tick(now)` once per requestAnimationFrame with the timestamp the
 * RAF callback received. The counter keeps a sliding window of recent
 * frame deltas and updates `label` ~4 times a second with:
 *   - average FPS (1000 / mean(dt))
 *   - worst frame time in the window (catches stutters that the mean hides)
 *
 * The counter intentionally avoids per-frame DOM writes — those would
 * themselves cause jank.
 */
export interface PerfCounter {
  tick(now: number): void;
}

export function createPerfCounter(label: HTMLElement): PerfCounter {
  const WINDOW = 120; // ~2s at 60fps
  const UPDATE_INTERVAL_MS = 250;
  const frames: number[] = [];
  let lastFrame = performance.now();
  let lastUpdate = lastFrame;

  return {
    tick(now: number) {
      const dt = now - lastFrame;
      lastFrame = now;
      frames.push(dt);
      if (frames.length > WINDOW) frames.shift();
      if (now - lastUpdate < UPDATE_INTERVAL_MS) return;
      lastUpdate = now;

      let sum = 0;
      let max = 0;
      for (const f of frames) {
        sum += f;
        if (f > max) max = f;
      }
      const avg = sum / frames.length;
      const fps = 1000 / avg;
      label.textContent = `${fps.toFixed(0)} fps · ${max.toFixed(1)}ms peak`;
    },
  };
}
